import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AppIconComponent } from './app-icon.component';

export type FormControlType = 'checkbox' | 'radio';

/**
 * Form Control microcomponent for checkbox and radio inputs
 * Replaces mat-checkbox and mat-radio with Tailwind CSS
 */
@Component({
  selector: 'app-form-control',
  standalone: true,
  imports: [CommonModule, AppIconComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppFormControlComponent),
      multi: true,
    },
  ],
  template: `
    <label
      [class]="getLabelClasses()"
      [attr.aria-disabled]="isDisabled"
      (click)="handleLabelClick($event)"
    >
      <input
        [type]="controlType"
        [name]="name"
        [value]="inputValue"
        [checked]="isChecked"
        [disabled]="isDisabled"
        [required]="isRequired"
        [class]="getInputClasses()"
        [attr.aria-describedby]="describedBy"
        (change)="handleChange($event)"
        (focus)="onTouched()"
        hidden
      />

      <!-- Custom Visual Control -->
      <div [class]="getControlClasses()">
        @if (controlType === 'checkbox' && isChecked) {
        <app-icon name="check" size="xs" class="text-white"> </app-icon>
        } @if (controlType === 'radio' && isChecked) {
        <div class="w-2 h-2 bg-white rounded-full"></div>
        }
      </div>

      <!-- Label Text -->
      @if (label) {
      <span [class]="getTextClasses()">{{ label }}</span>
      }

      <!-- Required Indicator -->
      @if (isRequired) {
      <span class="text-red-500 ml-1" aria-hidden="true">*</span>
      }
    </label>

    <!-- Helper Text -->
    @if (helperText) {
    <p [id]="describedBy" [class]="getHelperClasses()">
      {{ helperText }}
    </p>
    }

    <!-- Error Message -->
    @if (errorMessage) {
    <p
      [id]="describedBy + '-error'"
      class="mt-1 text-sm text-red-600"
      role="alert"
    >
      {{ errorMessage }}
    </p>
    }
  `,
})
export class AppFormControlComponent implements ControlValueAccessor {
  /** Control type */
  @Input({ required: true }) controlType!: FormControlType;

  /** Label text */
  @Input() label?: string;

  /** Input name attribute */
  @Input() name?: string;

  /** Input value (for radio buttons) */
  @Input() inputValue?: any;

  /** Whether the control is disabled */
  @Input() isDisabled: boolean = false;

  /** Whether the control is required */
  @Input() isRequired: boolean = false;

  /** Helper text */
  @Input() helperText?: string;

  /** Error message */
  @Input() errorMessage?: string;

  /** Control size */
  @Input() size: 'sm' | 'md' | 'lg' = 'md';

  /** Custom CSS classes */
  @Input() class: string = '';

  /** Change event emitter */
  @Output() valueChange = new EventEmitter<any>();

  // Internal state
  value: any = false;
  isChecked: boolean = false;
  describedBy: string = `control-${Math.random().toString(36).substr(2, 9)}`;

  // ControlValueAccessor implementation
  onChange = (value: any) => {};
  onTouched = () => {};

  writeValue(value: any): void {
    this.value = value;
    this.updateCheckedState();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  /**
   * Handle input change
   */
  handleChange(event: Event): void {
    const target = event.target as HTMLInputElement;

    if (this.controlType === 'checkbox') {
      this.value = target.checked;
    } else if (this.controlType === 'radio') {
      this.value = this.inputValue;
    }

    this.updateCheckedState();
    this.onChange(this.value);
    this.valueChange.emit(this.value);
  }

  /**
   * Handle label click
   */
  handleLabelClick(event: Event): void {
    if (this.isDisabled) {
      event.preventDefault();
    }
  }

  /**
   * Update checked state based on current value
   */
  private updateCheckedState(): void {
    if (this.controlType === 'checkbox') {
      this.isChecked = !!this.value;
    } else if (this.controlType === 'radio') {
      this.isChecked = this.value === this.inputValue;
    }
  }

  /**
   * Get label classes
   */
  getLabelClasses(): string {
    const baseClasses = [
      'inline-flex items-center cursor-pointer select-none',
      this.isDisabled ? 'opacity-50 cursor-not-allowed' : '',
      this.class,
    ];
    return baseClasses.filter(Boolean).join(' ');
  }

  /**
   * Get input classes (for screen readers)
   */
  getInputClasses(): string {
    return 'sr-only';
  }

  /**
   * Get control visual classes
   */
  getControlClasses(): string {
    const baseClasses = [
      'inline-flex items-center justify-center flex-shrink-0 border-2 transition-all duration-200',
      'focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500',
      this.getSizeClasses(),
      this.getShapeClasses(),
      this.getStateClasses(),
    ];
    return baseClasses.filter(Boolean).join(' ');
  }

  /**
   * Get text label classes
   */
  getTextClasses(): string {
    const sizeMap = {
      sm: 'text-sm ml-2',
      md: 'text-base ml-3',
      lg: 'text-lg ml-3',
    };

    const baseClasses = [
      sizeMap[this.size],
      this.isDisabled ? 'text-gray-400' : 'text-gray-900',
    ];

    return baseClasses.join(' ');
  }

  /**
   * Get helper text classes
   */
  getHelperClasses(): string {
    return `mt-1 text-sm ${
      this.errorMessage ? 'text-red-600' : 'text-gray-500'
    }`;
  }

  /**
   * Get size classes
   */
  private getSizeClasses(): string {
    const sizeMap = {
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-6 h-6',
    };
    return sizeMap[this.size];
  }

  /**
   * Get shape classes
   */
  private getShapeClasses(): string {
    return this.controlType === 'radio' ? 'rounded-full' : 'rounded';
  }

  /**
   * Get state-based classes
   */
  private getStateClasses(): string {
    if (this.errorMessage) {
      return this.isChecked
        ? 'bg-red-600 border-red-600'
        : 'border-red-300 bg-white hover:border-red-400';
    }

    return this.isChecked
      ? 'bg-primary-600 border-primary-600'
      : 'border-gray-300 bg-white hover:border-gray-400';
  }
}
